import React, { useState, useEffect, useCallback } from 'react';
import API from '../services/api';
import { Avatar, Typography, Space, Table, Alert, List, Modal, Button, Row, Col, Card, Statistic, Tag, Tooltip, Divider, Progress, Descriptions } from 'antd';
import { BellOutlined, CalendarOutlined, RightOutlined, UserOutlined, GlobalOutlined, ClockCircleOutlined, FireOutlined, SafetyCertificateOutlined, ThunderboltOutlined, SecurityScanOutlined, WarningOutlined, CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import TextPreview from '../components/TextPreview';
import { extractSummary } from '../utils/textParser';
import './UserHomePage.css';
import { useNavigate } from 'react-router-dom';
import { getUserRoleFromToken } from '../utils/auth';

const { Title, Text } = Typography;

const UserHomePage = () => {
  const [userData, setUserData] = useState(null);
  const [attackData, setAttackData] = useState([]);
  const [ipAddress, setIpAddress] = useState('');
  const [loginTime, setLoginTime] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [notices, setNotices] = useState([]);
  const [selectedNotice, setSelectedNotice] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [dataUpdateTime, setDataUpdateTime] = useState('');
  const [showPendingVerificationModal, setShowPendingVerificationModal] = useState(false);

  const navigate = useNavigate();

  const convertToBeijingTime = (utcTime) => {
    const date = new Date(utcTime);
    const options = {
      timeZone: 'Asia/Shanghai',
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    };
    const formattedTime = date.toLocaleString('zh-CN', options);
    return formattedTime.replace(/\//g, '-').replace(' ', ' ').replace(',', '');
  };

  const getRoundHourTime = () => {
    const now = new Date();
    const currentMinutes = now.getMinutes();
    
    // 根据当前分钟数判断应该显示整点还是半点
    if (currentMinutes < 30) {
      // 显示当前小时的整点
      now.setMinutes(0, 0, 0);
    } else {
      // 显示当前小时的半点
      now.setMinutes(30, 0, 0);
    }
    
    const options = {
      timeZone: 'Asia/Shanghai',
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    };
    const formattedTime = now.toLocaleString('zh-CN', options);
    return formattedTime
      .replace(/\//g, '-')
      .replace(/^(\d{4})-(\d{2})-(\d{2})/, '$1年$2月$3日');
  };

  const fetchUserData = useCallback(async () => {
    try {
      const userResponse = await API.get('/api/user/profile');
      setUserData(userResponse.data);
    } catch (error) {
      setErrorMessage('无法获取用户信息');
    }
  }, []);

  const fetchLoginIpAndTime = useCallback(async () => {
    try {
      const response = await API.get('/api/user/login-ip');
      setIpAddress(response.data.ip || '未知');
      setLoginTime(convertToBeijingTime(response.data.time || '未知'));
    } catch (error) {
      setErrorMessage('无法获取IP地址和登录时间');
    }
  }, []);

  const fetchAttackData = useCallback(async () => {
    try {
      const attackResponse = await API.get('/api/user/ip-all-attack');
      setAttackData(attackResponse.data.allAttacks || []);
    } catch (error) {
      if (
        error.response &&
        error.response.status === 404 &&
        error.response.data &&
        error.response.data.message === '没有找到攻击记录'
      ) {
        setAttackData([]); // 静默处理
      } else {
        console.error('获取攻击排行数据时出错:', error);
      }
    }
  }, []);

  const fetchNotices = useCallback(async () => {
    try {
      const noticeResponse = await API.get('/api/notice/notices');
      setNotices(noticeResponse.data.notices || []);
    } catch (error) {
      console.error('获取公告数据时出错:', error);
    }
  }, []);

  const handleShowNotice = (notice) => {
    setSelectedNotice(notice);
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
  };

  useEffect(() => {
    fetchUserData();
    fetchLoginIpAndTime();
    fetchAttackData();
    fetchNotices();
    setDataUpdateTime(getRoundHourTime());
  }, [fetchUserData, fetchLoginIpAndTime, fetchAttackData, fetchNotices]);

  // 检查是否需要显示待审核用户提示
  useEffect(() => {
    const userRole = getUserRoleFromToken();
    
    if (userRole === 'pending-verification') {
      const checkAndShowAlert = async () => {
        try {
          const token = localStorage.getItem('token');
          if (token) {
            // 解析token获取发布时间作为会话标识
            const payload = JSON.parse(atob(token.split('.')[1]));
            const currentTokenId = `${payload.iat}_${payload.exp}`;
            
            const lastAlertTokenId = localStorage.getItem('lastPendingVerificationAlertToken');
            
            if (currentTokenId !== lastAlertTokenId) {
              setShowPendingVerificationModal(true);
              localStorage.setItem('lastPendingVerificationAlertToken', currentTokenId);
            }
          }
        } catch (error) {
          // 备用方案：使用sessionStorage
          console.warn('token解析失败，使用sessionStorage方案:', error);
          const hasShownPendingAlert = sessionStorage.getItem('hasShownPendingVerificationAlert');
          if (!hasShownPendingAlert) {
            setShowPendingVerificationModal(true);
            sessionStorage.setItem('hasShownPendingVerificationAlert', 'true');
          }
        }
      };
      
      checkAndShowAlert();
    }
  }, []);

  // 关闭待审核用户提示框
  const handleClosePendingVerificationModal = () => {
    setShowPendingVerificationModal(false);
  };

  const attackColumns = [
    {
      title: '排名',
      key: 'rank',
      width: 80,
      align: 'center',
      render: (text, record, index) => {
        const rank = index + 1;
        const isTopThree = rank <= 3;
        
        return (
          <div style={{
            background: isTopThree ? '#ef4444' : '#f3f4f6',
            color: isTopThree ? 'white' : '#6b7280',
            borderRadius: '6px',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto',
            fontWeight: '600',
            fontSize: '14px'
          }}>
            {rank}
          </div>
        );
      }
    },
    {
      title: (
        <Space>
          <GlobalOutlined style={{ color: '#3b82f6' }} />
          <span>IP 地址</span>
        </Space>
      ),
      dataIndex: 'ip',
      key: 'ip',
      render: (text) => (
        <Tooltip title="点击查看详细攻击记录">
          <Tag
            color="processing"
            style={{
              padding: '6px 12px',
              borderRadius: '6px',
              fontSize: '13px',
              cursor: 'pointer',
              fontFamily: 'Monaco, monospace',
              fontWeight: '500',
              transition: 'all 0.2s ease',
              border: '1px solid #d1d5db'
            }}
            onClick={() => navigate(`/user/ip-attack-query?ip=${encodeURIComponent(text)}`)}
          >
            {text}
          </Tag>
        </Tooltip>
      ),
    },
    {
      title: (
        <Space>
          <FireOutlined style={{ color: '#ef4444' }} />
          <span>攻击次数</span>
        </Space>
      ),
      dataIndex: 'count',
      key: 'count',
      width: 180,
      render: (text, record, index) => {
        const maxCount = Math.max(...attackData.map(item => item.count));
        const percentage = maxCount > 0 ? (text / maxCount) * 100 : 0;
        const level = text > 100 ? 'critical' : text > 50 ? 'high' : 'medium';
        const colors = {
          critical: '#ef4444',
          high: '#f59e0b', 
          medium: '#10b981'
        };
        
        return (
          <div style={{ width: '100%' }}>
            <Space orientation="vertical" style={{ width: '100%' }} size="small">
              <Statistic
                value={text}
                valueStyle={{ 
                  color: colors[level],
                  fontWeight: '600',
                  fontSize: '16px',
                  fontFamily: 'Monaco, monospace'
                }}
                prefix={<FireOutlined style={{ color: colors[level] }} />}
                suffix={<span style={{ fontSize: '12px', color: '#6b7280' }}>次</span>}
              />
              <Progress
                percent={percentage}
                size={[undefined, 6]}
                strokeColor={colors[level]}
                trailColor="#f3f4f6"
                strokeLinecap="round"
                showInfo={false}
              />
            </Space>
          </div>
        );
      },
    },
  ];

  // 计算统计数据
  const totalAttacks = attackData.reduce((sum, item) => sum + item.count, 0);
  const threatLevel = totalAttacks > 1000 ? 'high' : totalAttacks > 100 ? 'medium' : 'low';
  const securityStatus = threatLevel === 'low' ? 'secure' : threatLevel === 'medium' ? 'warning' : 'danger';

  return (
    <div
      className="user-homepage"
      style={{
        padding: '24px',
        background: '#f8f9fa',
        minHeight: '100vh',
        maxWidth: '100vw',
        overflowX: 'hidden'
      }}
    >
      {/* 页面头部 */}
      <div style={{ marginBottom: '32px' }}>
        <Title level={1} style={{ 
          color: '#2d3748', 
          margin: 0,
          textAlign: 'center',
          fontWeight: '600',
          fontSize: '2.2rem'
        }}>
          DDoS 防护监控中心
        </Title>
        <Text style={{ 
          display: 'block',
          textAlign: 'center',
          color: '#6b7280',
          fontSize: '16px',
          marginTop: '8px'
        }}>
          实时监控 · 智能防护 · 安全可靠
        </Text>
      </div>

      {/* 统计概览卡片 */}
      <Row gutter={[24, 24]} style={{ marginBottom: '32px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card className="stat-card" style={{
            background: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            height: '120px'
          }}>
            <Statistic
              title={<span style={{ color: '#6b7280', fontSize: '14px' }}>今日攻击总数</span>}
              value={totalAttacks}
              prefix={<SecurityScanOutlined style={{ color: '#ef4444' }} />}
              valueStyle={{ color: '#ef4444', fontSize: '24px', fontWeight: '600' }}
            />
          </Card>
        </Col>
        
        <Col xs={24} sm={12} lg={6}>
          <Card className="stat-card" style={{
            background: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            height: '120px'
          }}>
            <Statistic
              title={<span style={{ color: '#6b7280', fontSize: '14px' }}>被攻击IP数</span>}
              value={attackData.length}
              prefix={<GlobalOutlined style={{ color: '#3b82f6' }} />}
              valueStyle={{ color: '#3b82f6', fontSize: '24px', fontWeight: '600' }}
            />
          </Card>
        </Col>
        
        <Col xs={24} sm={12} lg={6}>
          <Card className="stat-card" style={{
            background: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            height: '120px'
          }}>
            <Statistic
              title={<span style={{ color: '#6b7280', fontSize: '14px' }}>安全状态</span>}
              value={securityStatus === 'secure' ? '安全' : securityStatus === 'warning' ? '警告' : '危险'}
              prefix={securityStatus === 'secure' ? <CheckCircleOutlined style={{ color: '#10b981' }} /> : 
                      securityStatus === 'warning' ? <WarningOutlined style={{ color: '#f59e0b' }} /> : 
                      <SecurityScanOutlined style={{ color: '#ef4444' }} />}
              valueStyle={{ 
                color: securityStatus === 'secure' ? '#10b981' : 
                       securityStatus === 'warning' ? '#f59e0b' : '#ef4444', 
                fontSize: '24px', 
                fontWeight: '600' 
              }}
            />
          </Card>
        </Col>
        
        <Col xs={24} sm={12} lg={6}>
          <Card className="stat-card" style={{
            background: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            height: '120px'
          }}>
            <Statistic
              title={<span style={{ color: '#6b7280', fontSize: '14px' }}>系统状态</span>}
              value="运行中"
              prefix={<CheckCircleOutlined style={{ color: '#10b981' }} />}
              valueStyle={{ color: '#10b981', fontSize: '24px', fontWeight: '600' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 用户信息卡片 */}
      <Row justify="center" className="mb-4">
        <Col xs={24}>
          <Card
            className="user-profile-card"
            style={{ 
              background: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              marginBottom: '32px'
            }}
          >
            <Row gutter={[32, 32]} align="middle" style={{ padding: '24px 0' }}>
              <Col xs={24} md={8} lg={6} style={{ textAlign: 'center' }}>
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <Avatar
                    size={100}
                    src={userData?.qq ? `https://q1.qlogo.cn/g?b=qq&nk=${userData.qq}&s=100` : 'https://tc-oss-2.1yidc.com/2024/09/22/66efb4abe7c54.jpg'}
                    className="user-avatar"
                    icon={<UserOutlined />}
                    style={{ 
                      border: '3px solid #e5e7eb'
                    }}
                  />
                  <div style={{
                    position: 'absolute',
                    top: '-5px',
                    right: '-5px',
                    background: '#10b981',
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px solid white'
                  }}>
                    <SafetyCertificateOutlined style={{ color: 'white', fontSize: '12px' }} />
                  </div>
                </div>
              </Col>
              <Col xs={24} md={16} lg={18}>
                <Row gutter={[24, 24]}>
                  <Col xs={24} lg={12}>
                    <div>
                      <Title level={2} style={{ 
                        color: '#2d3748',
                        margin: '0 0 8px 0',
                        fontWeight: '600'
                      }}>
                        {userData?.username || '用户名'}
                      </Title>
                      <Text style={{ 
                        color: '#6b7280', 
                        fontSize: '16px',
                        display: 'block',
                        marginBottom: '24px'
                      }}>
                        欢迎使用DDoS流量监控系统
                      </Text>
                      <Space size="large">
                        <div>
                          <GlobalOutlined style={{ color: '#6b7280', marginRight: '8px' }} />
                          <Text style={{ color: '#374151', fontFamily: 'Monaco, monospace' }}>
                            {ipAddress || '获取中...'}
                          </Text>
                        </div>
                        <div>
                          <ClockCircleOutlined style={{ color: '#6b7280', marginRight: '8px' }} />
                          <Text style={{ color: '#374151' }}>
                            {loginTime || '获取中...'}
                          </Text>
                        </div>
                      </Space>
                    </div>
                  </Col>
                  <Col xs={24} lg={12}>
                    <Card
                      size="small"
                      style={{
                        background: '#f9fafb',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px'
                      }}
                    >
                      <Descriptions 
                        size="small" 
                        column={1}
                        styles={{
                          label: { color: '#6b7280' },
                          content: { color: '#374151', fontWeight: '500' }
                        }}
                      >
                        <Descriptions.Item label="用户类型">
                          {userData?.accountType === 'ram' ? 'RAM用户' :
                            userData?.accountType === 'main' ? (
                           userData?.role === 'wlj-user' ? '物理机用户' : 
                                userData?.role === 'user' ? '普通用户' : 
                                userData?.role === 'pending-verification' ? '待审核用户' : '未知'
                            ) : '未知'}
                        </Descriptions.Item>
                        <Descriptions.Item label="账户状态">
                          {userData?.status === 'active' ? '正常' : 
                           userData?.status === 'disabled' ? '禁用' : '未知'}
                        </Descriptions.Item>
                        <Descriptions.Item label="权限级别">
                          {userData?.accountType === 'ram' ? '受限' :
                            userData?.accountType === 'main' ? (
                           userData?.role === 'user' ? '高级' : 
                                userData?.role === 'wlj-user' ? '普通' : 
                                userData?.role === 'pending-verification' ? '无权限' : '未知'
                            ) : '未知'}
                        </Descriptions.Item>
                      </Descriptions>
                    </Card>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* 错误信息 */}
      {errorMessage && (
        <Row justify="center" className="mb-4">
          <Col xs={24}>
            <Alert
              message={errorMessage}
              type="error"
              showIcon
              action={
                <Button 
                  size="small" 
                  type="primary" 
                  danger
                >
                  重试
                </Button>
              }
              style={{ 
                borderRadius: '6px',
                marginBottom: '24px'
              }}
            />
          </Col>
        </Row>
      )}

      {/* 主要内容区域 */}
      <Row gutter={[24, 24]}>
        <Col xs={24} lg={14}>
          <Card
            title={
              <Space>
                <div style={{
                  background: '#3b82f6',
                  borderRadius: '6px',
                  padding: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <ThunderboltOutlined style={{ color: 'white', fontSize: '14px' }} />
                </div>
                <span style={{ fontSize: '16px', fontWeight: '600', color: '#2d3748' }}>威胁检测实时统计</span>
              </Space>
            }
            extra={
              <Space>
                <Tag color="red" style={{ fontSize: '12px' }}>当日监控(更新周期:30分钟)</Tag>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  更新: {dataUpdateTime}
                </Text>
              </Space>
            }
            style={{ 
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              background: '#ffffff',
              height: '580px'
            }}
          >
            <div style={{ height: 480 }}>
              <Table
                columns={attackColumns}
                dataSource={attackData}
                rowKey={(record) => record.ip}
                pagination={{ pageSize: 5, size: 'small' }}
                size="small"
                locale={{
                  emptyText: (
                    <div style={{ 
                      textAlign: 'center', 
                      padding: '60px 20px'
                    }}>
                      <div style={{
                        background: '#10b981',
                        borderRadius: '50%',
                        width: '80px',
                        height: '80px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 20px'
                      }}>
                        <CheckCircleOutlined style={{ fontSize: 36, color: 'white' }} />
                      </div>
                      <Title level={4} style={{ color: '#10b981', margin: '0 0 8px 0' }}>
                        系统运行正常
                      </Title>
                      <Text style={{ color: '#6b7280' }}>
                        暂无安全威胁检测到
                      </Text>
                    </div>
                  )
                }}
                className="attack-table"
              />
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={10}>
          <Card
            title={
              <Space>
                <div style={{
                  background: '#3b82f6',
                  borderRadius: '6px',
                  padding: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <BellOutlined style={{ color: 'white', fontSize: '14px' }} />
                </div>
                <span style={{ fontSize: '16px', fontWeight: '600', color: '#2d3748' }}>系统公告</span>
              </Space>
            }
            className="notice-section"
            extra={
              <Tag color="blue" style={{ fontSize: '12px' }}>
                最新消息
              </Tag>
            }
            style={{ 
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              background: '#ffffff',
              height: '580px'
            }}
          >
            <div style={{ 
              height: 480, 
              overflowY: 'auto', 
              overflowX: 'hidden',
              paddingRight: '4px',
              marginRight: '-4px'
            }}>
              <List
                dataSource={notices}
                renderItem={(notice, index) => (
                  <List.Item
                    className="notice-item"
                    onClick={() => handleShowNotice(notice)}
                    style={{ 
                      cursor: 'pointer',
                      padding: '16px',
                      marginBottom: '12px',
                      marginRight: '4px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      background: '#ffffff',
                      transition: 'all 0.2s ease',
                      width: 'calc(100% - 4px)',
                      boxSizing: 'border-box'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#d1d5db';
                      e.currentTarget.style.background = '#f9fafb';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.background = '#ffffff';
                    }}
                  >
                    <div style={{ width: '100%', overflow: 'hidden' }}>
                      <Space orientation="vertical" style={{ width: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                          <Title level={5} style={{ 
                            margin: 0, 
                            color: '#2d3748',
                            fontSize: '15px',
                            flex: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontWeight: '600'
                          }}>
                            {notice.title}
                          </Title>
                          {index < 3 && (
                            <Tag size="small" color="red" style={{ 
                              flexShrink: 0, 
                              marginLeft: '8px'
                            }}>
                              置顶
                            </Tag>
                          )}
                        </div>
                        <div style={{ 
                          margin: '8px 0 0 0',
                          color: '#6b7280',
                          fontSize: '13px',
                          width: '100%',
                          wordBreak: 'break-word',
                          lineHeight: '1.5'
                        }}>
                          <Text ellipsis={{ rows: 2, expandable: false }} style={{
                            color: '#6b7280',
                            fontSize: '13px',
                            display: 'block'
                          }}>
                            {extractSummary(notice.content, 80)}
                          </Text>
                        </div>
                        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                          <Space style={{ overflow: 'hidden', flex: 1 }}>
                            <CalendarOutlined style={{ color: '#9ca3af', fontSize: '12px' }} />
                            <Text type="secondary" style={{ fontSize: '12px', color: '#9ca3af' }}>{notice.date}</Text>
                          </Space>
                          <RightOutlined style={{ color: '#9ca3af', fontSize: '12px', flexShrink: 0 }} />
                        </Space>
                      </Space>
                    </div>
                  </List.Item>
                )}
              />
            </div>
          </Card>
        </Col>
      </Row>

      {/* 公告详情弹窗 */}
      <Modal
        title={
          <Space>
            <div style={{
              background: '#3b82f6',
              borderRadius: '6px',
              padding: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <BellOutlined style={{ color: 'white', fontSize: '14px' }} />
            </div>
            <span style={{ fontSize: '16px', fontWeight: '600' }}>{selectedNotice?.title}</span>
          </Space>
        }
        open={isModalVisible}
        onCancel={handleCloseModal}
        footer={[
          <Button 
            key="close" 
            onClick={handleCloseModal}
            type="primary"
            style={{
              background: '#3b82f6',
              border: 'none',
              borderRadius: '6px',
              height: '36px',
              paddingLeft: '20px',
              paddingRight: '20px'
            }}
          >
            关闭
          </Button>
        ]}
        centered
        width={800}
        style={{
          borderRadius: '8px'
        }}
      >
        <Space orientation="vertical" style={{ width: '100%' }}>
          <Space style={{ padding: '8px 0' }}>
            <CalendarOutlined style={{ color: '#3b82f6' }} />
            <Text style={{ color: '#6b7280' }}>发布日期: {selectedNotice?.date}</Text>
          </Space>
          <Divider style={{ margin: '16px 0' }} />
          <div style={{ minHeight: '200px' }}>
            <TextPreview 
              content={selectedNotice?.content || ''} 
              title="" 
              showTypeTag={false}
              maxHeight={400}
              style={{ 
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                background: '#f9fafb'
              }}
            />
          </div>
        </Space>
      </Modal>
      
      {/* 待审核用户提示弹窗 */}
      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: '16px' }} />
            <span style={{ fontSize: '16px', fontWeight: '600' }}>账号审核提醒</span>
          </Space>
        }
        open={showPendingVerificationModal}
        onCancel={handleClosePendingVerificationModal}
        footer={[
          <Button 
            key="confirm" 
            type="primary"
            onClick={handleClosePendingVerificationModal}
            style={{ borderRadius: '4px' }}
          >
            我知道了
          </Button>
        ]}
        centered
        width={500}
        maskClosable={false}
        closable={false}
      >
        <div style={{ padding: '20px 0', lineHeight: '1.6' }}>
                <div style={{
            display: 'flex', 
            alignItems: 'center', 
            marginBottom: '20px',
            padding: '16px',
            background: '#fff7e6',
            border: '1px solid #ffd591',
            borderRadius: '6px'
          }}>
            <ExclamationCircleOutlined style={{ 
              color: '#fa8c16', 
              fontSize: '18px', 
              marginRight: '12px',
              flexShrink: 0
            }} />
            <div>
              <div style={{ fontWeight: '600', color: '#ad6800', marginBottom: '4px' }}>
                当前账号状态：待审核
              </div>
              <div style={{ fontSize: '14px', color: '#ad6800' }}>
                当前账号暂无业务操作权限，无法查看流量数据和攻击记录
              </div>
            </div>
          </div>
          
          <div style={{ fontSize: '14px', color: '#333', marginBottom: '16px' }}>
            <div style={{ fontWeight: '600', marginBottom: '12px', color: '#1890ff' }}>
              如需开通业务权限，请按以下步骤操作：
            </div>
            <div style={{ paddingLeft: '16px' }}>
              <p style={{ margin: '0 0 8px 0' }}>
                • 前往"我的IP权限"页面提交IP权限申请
              </p>
              <p style={{ margin: '0 0 8px 0' }}>
                • 等待管理员审核通过并分配IP资源
                  </p>
              <p style={{ margin: '0', color: '#52c41a' }}>
                • 如需加速审核，可联系服务商协助处理
                  </p>
                </div>
              </div>
        </div>
      </Modal>
    </div>
  );
};

export default UserHomePage;
